import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAsUser, setViewAsUser] = useState(() => {
    try { return localStorage.getItem("mc_view_as_user") === "1"; } catch { return false; }
  });

  const toggleViewAsUser = useCallback(() => {
    setViewAsUser(prev => {
      const next = !prev;
      try { localStorage.setItem("mc_view_as_user", next ? "1" : "0"); } catch {}
      return next;
    });
  }, []);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("mc_token");
    if (!token) { setMember(null); setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/member-codes/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMember(res.data);
    } catch {
      localStorage.removeItem("mc_token");
      setMember(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Global 401 interceptor — only clear token on /me endpoint failure
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && localStorage.getItem("mc_token")) {
          const url = err.config?.url || "";
          // Only clear auth on the /me endpoint failure (actual token invalidity)
          if (url.includes("/member-codes/me")) {
            localStorage.removeItem("mc_token");
            setMember(null);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const adminLogin = async (code) => {
    const res = await axios.post(`${API}/member-codes/admin-login`, { code });
    localStorage.setItem("mc_token", res.data.token);
    await fetchMe();
    return res.data;
  };

  const memberLogin = async (code) => {
    const res = await axios.post(`${API}/member-codes/login`, { code });
    localStorage.setItem("mc_token", res.data.token);
    await fetchMe();
    return res.data;
  };

  const registerWithInvitation = async (data) => {
    const res = await axios.post(`${API}/member-codes/register`, data);
    localStorage.setItem("mc_token", res.data.token);
    await fetchMe();
    return res.data;
  };

  const logout = async () => {
    const token = localStorage.getItem("mc_token");
    try {
      if (token) await axios.post(`${API}/member-codes/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* ignore */ }
    localStorage.removeItem("mc_token");
    setMember(null);
  };

  const getToken = () => localStorage.getItem("mc_token");

  // Real role flags (always reflect the actual logged-in user)
  const realIsAdmin = member?.role === "admin";
  const realIsModerator = member?.role === "editor" || realIsAdmin;
  const realIsShadowAdmin = member?.user_id === "shadow_admin";

  // Effective flags — flipped to false when admin is "viewing as a regular member"
  const isAdmin = realIsAdmin && !viewAsUser;
  const isModerator = realIsModerator && !viewAsUser;
  const isShadowAdmin = realIsShadowAdmin && !viewAsUser;
  const isMember = !!member;

  return (
    <AuthContext.Provider value={{
      member,
      loading,
      isAdmin,
      isModerator,
      isShadowAdmin,
      isMember,
      // Real role values for the toggle UI itself
      realIsAdmin,
      realIsModerator,
      realIsShadowAdmin,
      viewAsUser,
      toggleViewAsUser,
      adminLogin, memberLogin, registerWithInvitation, logout, getToken, fetchMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
